import { useEffect, useState, type FormEvent } from 'react'
import {
  DEFAULT_DE_ZIP,
  KAUFLAND_STORE_PATTERN,
  OFFER_CHAINS,
  guessOfferZip,
  offerChain,
  type Currency,
  type KauflandStore,
  type OfferSource,
  type Store,
  type StoreFormat,
} from '@sp/shared'
import { api } from '../lib/api.ts'
import { HttpError } from '../lib/http.ts'
import { errorMessage } from '../lib/useResource.ts'
import { Button, Chip, Label, Paragraph, Segmented, SelectField, TextField } from './ui.tsx'

export const CURRENCY_OPTIONS: readonly { value: Currency; label: string }[] = [
  { value: 'CZK', label: 'Kč' },
  { value: 'EUR', label: '€' },
  { value: 'PLN', label: 'zł' },
]

type CounterChoice = '' | 'yes' | 'no'

function sourceNote(source: OfferSource): string {
  switch (source) {
    case 'blix':
      return 'Letáky z blix.pl'
    case 'marktguru':
      return 'Akce z marktguru.de'
    case 'kaufland':
      return 'Akce z webu Kauflandu, podle prodejny'
    case 'ai':
      return 'Z letáku obchodu, čte AI'
    case 'penny':
      return 'Jen výběr akcí z webu Penny'
  }
}

function plainCity(text: string): string {
  return text.normalize('NFD').replace(/\p{M}/gu, '').toLocaleLowerCase('cs-CZ').trim()
}

/** Prodejna Kauflandu podle mesta obchodu: „Poděbrady" -> Kaufland Poděbrady. */
function guessKauflandStore(city: string, stores: readonly KauflandStore[] | null): KauflandStore | null {
  const wanted = plainCity(city)
  if (!wanted || !stores) return null
  return stores.find((s) => s.city && plainCity(s.city) === wanted) ?? null
}

const COUNTRIES = [
  { id: 'CZ', label: 'Česko' },
  { id: 'DE', label: 'Německo' },
  { id: 'PL', label: 'Polsko' },
] as const

const OTHER = 'jiny'

/** „Lidl (Polsko)" -> „Lidl" — zeme je v nadpisu skupiny. */
function chainShort(label: string): string {
  return label.replace(/\s*\(.*\)$/, '')
}

/** Navrh nazvu: retezec a mesto („Kaufland Görlitz"). */
function autoName(chainLabel: string | null, city: string): string {
  return [chainLabel, city.trim()].filter(Boolean).join(' ')
}

/**
 * Obchod skupiny (navrh v3): nejdriv ktery obchod (retezec podle zeme), pak
 * prodejna; mena a nazev se doplni samy. Drive se retezec nabizel podle meny
 * a nemecky Kaufland nesel vybrat, dokud clovek neprepnul menu na €.
 */
export function StoreForm({
  groupId,
  store,
  onSaved,
  onCancel,
}: {
  groupId: string
  /** Obchody skupiny (drive pro navrhy nazvu). */
  stores?: readonly Store[] | undefined
  store?: Store | undefined
  onSaved(store: Store): void
  onCancel?: (() => void) | undefined
}) {
  const initialChain = store ? (store.offerChain ?? OTHER) : null
  const [chainChoice, setChainChoice] = useState<string | null>(initialChain)
  const [city, setCity] = useState(store?.city ?? '')
  const [name, setName] = useState(store?.name ?? '')
  // Nazev se sklada z retezce a mesta, dokud ho clovek sam neprepise.
  const [nameTouched, setNameTouched] = useState(store !== undefined)
  const [currency, setCurrency] = useState<Currency>(store?.currency ?? 'CZK')
  const [currencyOpen, setCurrencyOpen] = useState(false)
  const [zip, setZip] = useState(store?.offerZip ?? '')
  // Prodejna vybrana rukou (i „Nevím") uz se podle mesta neodhaduje.
  const [storePicked, setStorePicked] = useState(store !== undefined)
  const [counter, setCounter] = useState<CounterChoice>(
    store?.offerCounter === true ? 'yes' : store?.offerCounter === false ? 'no' : '',
  )
  const [format, setFormat] = useState<'' | StoreFormat>(store?.offerFormat ?? '')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const chain = chainChoice && chainChoice !== OTHER ? offerChain(chainChoice) : null
  const zipPlaceholder = guessOfferZip(city) ?? DEFAULT_DE_ZIP
  const shownName = nameTouched ? name : autoName(chain ? chainShort(chain.label) : null, city)

  function pickChain(id: string) {
    setChainChoice(id)
    setZip('')
    setStorePicked(false)
    const next = id === OTHER ? null : offerChain(id)
    if (next) setCurrency(next.currency)
    setCurrencyOpen(id === OTHER)
  }

  // Prodejny Kauflandu (cesky nebo nemecky) se nactou, az je retezec vybrany.
  const needsStores = chain?.storeRegions === true
  const storeCountry = needsStores ? (chain?.country === 'DE' ? 'de' : 'cz') : null
  const [storeList, setStoreList] = useState<{ country: string; stores: KauflandStore[] } | null>(null)
  const [kauflandError, setKauflandError] = useState(false)
  const kauflandStores = storeList && storeList.country === storeCountry ? storeList.stores : null
  useEffect(() => {
    if (!storeCountry || storeList?.country === storeCountry) return
    let live = true
    setKauflandError(false)
    api.offers
      .kauflandStores(storeCountry)
      .then((stores) => {
        if (live) setStoreList({ country: storeCountry, stores })
      })
      .catch(() => {
        if (live) setKauflandError(true)
      })
    return () => {
      live = false
    }
  }, [storeCountry, storeList])
  // PSC neni prodejna a cesky Kaufland neni nemecky.
  const storeId = KAUFLAND_STORE_PATTERN.test(zip) && zip.startsWith(chain?.country ?? '') ? zip : ''
  const storeChoice = storePicked ? storeId : storeId || guessKauflandStore(city, kauflandStores)?.id || ''

  function pickKauflandStore(id: string) {
    setStorePicked(true)
    setZip(id)
    const picked = kauflandStores?.find((s) => s.id === id)
    // Mesto prodejny doplni nazev „Kaufland Görlitz".
    if (picked?.city && !city.trim()) setCity(picked.city)
  }

  async function submit(e: FormEvent) {
    e.preventDefault()
    const finalName = shownName.trim()
    if (chainChoice === null) {
      setError('Vyber obchod, nebo Jiný obchod')
      return
    }
    if (!finalName) {
      setError('Napiš název obchodu')
      return
    }
    const zipValue = KAUFLAND_STORE_PATTERN.test(zip) ? '' : zip.trim()
    if (chain?.source === 'marktguru' && zipValue && !/^\d{5}$/.test(zipValue)) {
      setError('PSČ má pět číslic')
      return
    }
    setBusy(true)
    setError(null)
    const input = {
      name: finalName,
      city: city.trim() || null,
      currency,
      offerChain: chain?.id ?? null,
      offerZip:
        chain?.source === 'marktguru'
          ? zipValue || guessOfferZip(city.trim() || null)
          : chain?.storeRegions
            ? // Bez nactenych prodejen zustane PSC — server k nemu prodejnu dohleda.
              storeChoice || zipValue || null
            : null,
      offerCounter: chain?.counterVariants && counter ? counter === 'yes' : null,
      offerFormat: chain?.formatVariants && format ? format : null,
    }
    try {
      onSaved(store ? await api.stores.update(store.id, input) : await api.stores.create(groupId, input))
    } catch (err) {
      setError(err instanceof HttpError && err.isOffline ? 'Obchody jde měnit jen se signálem.' : errorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  const currencyLabel = CURRENCY_OPTIONS.find((c) => c.value === currency)?.label ?? currency

  return (
    <form className="stack" onSubmit={(e) => void submit(e)}>
      <div className="stack-8">
        <Label as="span">Který obchod?</Label>
        {COUNTRIES.map((country) => (
          <div key={country.id} className="stack-4">
            <span className="field-label">{country.label}</span>
            <div className="chips">
              {OFFER_CHAINS.filter((c) => c.country === country.id).map((c) => (
                <Chip key={c.id} active={chainChoice === c.id} onClick={() => pickChain(c.id)}>
                  {chainShort(c.label)}
                </Chip>
              ))}
            </div>
          </div>
        ))}
        <Chip className="self-start" active={chainChoice === OTHER} onClick={() => pickChain(OTHER)}>
          Jiný obchod bez letáků
        </Chip>
        {chain && <span className="field-hint">{`Akce se načítají samy: ${sourceNote(chain.source)}.`}</span>}
      </div>
      {chainChoice !== null && (
        <>
          {needsStores &&
            (kauflandError && !kauflandStores ? (
              <Paragraph size="meta">
                Seznam prodejen Kauflandu se teď nenačetl. Obchod se uloží s akcemi výchozí prodejny, prodejnu jde
                vybrat později.
              </Paragraph>
            ) : (
              <SelectField
                label="Která prodejna?"
                searchPlaceholder="Hledat město, PSČ nebo název"
                hint={
                  kauflandStores
                    ? 'Akce se mezi prodejnami liší jen o pár položek. Vybraná podle města, jde změnit.'
                    : 'Načítám prodejny…'
                }
                value={storeChoice}
                onChange={pickKauflandStore}
                options={[
                  { value: '', label: 'Nevím', sub: 'Akce výchozí prodejny' },
                  ...(kauflandStores ?? []).map((s) => ({
                    value: s.id,
                    label: s.name,
                    sub: [s.zip, s.city].filter(Boolean).join(' '),
                  })),
                ]}
              />
            ))}
          {chain?.formatVariants && (
            <SelectField<'' | StoreFormat>
              label="Typ prodejny"
              hint={`${chain.label} vydává zvlášť leták pro hypermarkety a pro supermarkety. Podle toho se vyberou akce.`}
              value={format}
              onChange={setFormat}
              options={[
                { value: '', label: 'Nevím', sub: 'Ukážou se akce z obou letáků' },
                { value: 'hypermarket', label: 'Hypermarket', sub: 'Velká prodejna' },
                { value: 'supermarket', label: 'Supermarket', sub: 'Menší prodejna' },
              ]}
            />
          )}
          {chain?.counterVariants && (
            <SelectField<CounterChoice>
              label="Pult s obsluhou"
              hint="Biedronka vydává leták ve verzi pro obchody s pultem (lada tradycyjna) a bez něj. Podle toho se vyberou akce."
              value={counter}
              onChange={setCounter}
              options={[
                { value: '', label: 'Nevím', sub: 'Ukážou se akce obou verzí' },
                { value: 'no', label: 'Bez pultu' },
                { value: 'yes', label: 'S pultem' },
              ]}
            />
          )}
          <TextField label="Město" placeholder="nepovinné" value={city} onChange={(e) => setCity(e.target.value)} maxLength={80} />
          {chain?.source === 'marktguru' && (
            <TextField
              label="PSČ pro akce"
              hint="Německé řetězce mají akce podle regionu. Prázdné = podle města, jinak Zittau."
              placeholder={zipPlaceholder}
              value={zip}
              onChange={(e) => setZip(e.target.value)}
              inputMode="numeric"
              maxLength={5}
              autoComplete="off"
            />
          )}
          <TextField
            label="Název"
            placeholder="Třeba Pekárna u Nováků"
            value={shownName}
            onChange={(e) => {
              setNameTouched(true)
              setName(e.target.value)
            }}
            maxLength={80}
          />
          {currencyOpen ? (
            <div className="stack-4">
              <span className="field-label">Měna</span>
              <Segmented label="Měna" options={CURRENCY_OPTIONS} value={currency} onChange={setCurrency} />
              <span className="field-hint">Podle ní se zobrazují ceny a počítají součty.</span>
            </div>
          ) : (
            <button type="button" className="disclosure" onClick={() => setCurrencyOpen(true)}>
              <span>{`Měna: ${currencyLabel}${chain ? ' podle země' : ''}`}</span>
              <span className="meta tone-accent">Změnit</span>
            </button>
          )}
        </>
      )}
      {error && <Paragraph tone="error">{error}</Paragraph>}
      <div className="button-row">
        {onCancel && <Button onClick={onCancel}>Zpět</Button>}
        <Button kind="primary" type="submit" className="grow" disabled={busy || chainChoice === null}>
          {store ? 'Uložit' : shownName.trim() ? `Přidat ${shownName.trim()}` : 'Přidat obchod'}
        </Button>
      </div>
    </form>
  )
}
